// MessagePage.jsx

import { useMemo, useState, useEffect, useRef } from "react";
import Editor from "@monaco-editor/react";
import PropTypes from "prop-types";
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Divider,
  Chip,
  Tooltip,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import TitleIcon from "@mui/icons-material/Title";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import InsertLinkIcon from "@mui/icons-material/InsertLink";

import MainPanelContent from "../components/MainPanelContent";
import FieldLabel from "../components/FieldLabel";
import { useUsers } from "../../../hooks/useUsers";
import { supabaseAuthenticatedApi } from "../../../components/SupabaseClient";
import { renderMessageHtml } from "../../../utils/markdownRenderer";
import {
  EDITOR_THEME_LIGHT_NAME,
  defineLightEditorTheme,
} from "../../../theme/codeTheme";

const DRAFT_KEY = "efabric_message_draft";
const MESSAGE_EDITOR_HEIGHT = 420;
const MONO_FONT =
  '"JetBrains Mono", "Fira Mono", "Cascadia Code", "Menlo", "Consolas", monospace';
const EDITOR_ACTIONS = {
  bold: "efabric.message.bold",
  italic: "efabric.message.italic",
  heading: "efabric.message.heading",
  bulletList: "efabric.message.bullet-list",
  numberedList: "efabric.message.numbered-list",
  link: "efabric.message.link",
};

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveDraft(draft) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // ignore quota / serialization errors
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
}

function userLabel(u) {
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return name ? `${name} <${u.email}>` : u.email;
}

function parseMarkdownListLine(lineText) {
  const numberedMatch = lineText.match(/^(\s*)(\d+)\.\s(.*)$/);
  if (numberedMatch) {
    const [, indent, number, content] = numberedMatch;
    return {
      currentPrefix: `${indent}${number}. `,
      nextPrefix: `${indent}${Number(number) + 1}. `,
      isEmpty: content.trim() === "",
    };
  }

  const bulletMatch = lineText.match(/^(\s*)([-*+])\s(.*)$/);
  if (bulletMatch) {
    const [, indent, marker, content] = bulletMatch;
    return {
      currentPrefix: `${indent}${marker} `,
      nextPrefix: `${indent}${marker} `,
      isEmpty: content.trim() === "",
    };
  }

  return null;
}

const MessagePage = ({ user }) => {
  const { users, loading, error } = useUsers();

  // Restore any saved draft once, synchronously, to avoid clobbering it.
  const draftRef = useRef(null);
  if (draftRef.current === null) draftRef.current = loadDraft();

  const [selectedUserIds, setSelectedUserIds] = useState(
    () => draftRef.current.recipientIds || [],
  );
  const [subject, setSubject] = useState(() => draftRef.current.subject || "");
  const [body, setBody] = useState(() => draftRef.current.body || "");

  const [sending, setSending] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [expandedRecipients, setExpandedRecipients] = useState(false);
  const [bodyTab, setBodyTab] = useState("write"); // 'write' | 'preview'
  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  const COLLAPSED_TAGS = 8;

  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    msg: "",
  });

  const selectedUsers = useMemo(
    () => users.filter((u) => selectedUserIds.includes(u.id)),
    [users, selectedUserIds],
  );

  const admins = useMemo(
    () => users.filter((u) => u.permission_level === "admin"),
    [users],
  );
  const nonAdmins = useMemo(
    () => users.filter((u) => u.permission_level !== "admin"),
    [users],
  );

  // Auto-save the draft whenever the composer changes.
  useEffect(() => {
    saveDraft({ recipientIds: selectedUserIds, subject, body });
  }, [selectedUserIds, subject, body]);

  const hasDraft = Boolean(
    selectedUserIds.length || subject.trim() || body.trim(),
  );
  const recipientsError = showErrors && selectedUsers.length === 0;
  const subjectError = showErrors && !subject.trim();
  const bodyError = showErrors && !body.trim();

  // Which quick-add group (if any) the current selection exactly matches.
  const matchesGroup = (list) =>
    list.length > 0 &&
    list.length === selectedUserIds.length &&
    list.every((u) => selectedUserIds.includes(u.id));

  const activeGroup = matchesGroup(users)
    ? "everyone"
    : matchesGroup(admins)
      ? "admins"
      : matchesGroup(nonAdmins)
        ? "users"
        : null;

  const handleQuickAdd = (group) => {
    if (group === "everyone") setSelectedUserIds(users.map((u) => u.id));
    else if (group === "admins") setSelectedUserIds(admins.map((u) => u.id));
    else if (group === "users") setSelectedUserIds(nonAdmins.map((u) => u.id));
  };

  const handleDiscard = () => {
    setSelectedUserIds([]);
    setSubject("");
    setBody("");
    setShowErrors(false);
    clearDraft();
  };

  const applyWrap = (before, after = before) => {
    const editorInstance = editorRef.current;
    const monaco = monacoRef.current;
    const model = editorInstance?.getModel();
    const selection = editorInstance?.getSelection();
    if (!editorInstance || !monaco || !model || !selection) return;

    const startOffset = model.getOffsetAt(selection.getStartPosition());
    const endOffset = model.getOffsetAt(selection.getEndPosition());
    const selected = model.getValueInRange(selection);
    const nextText = `${before}${selected}${after}`;
    const nextStart = startOffset + before.length;
    const nextEnd = nextStart + selected.length;

    editorInstance.pushUndoStop();
    editorInstance.executeEdits("message-markdown-wrap", [
      {
        range: selection,
        text: nextText,
        forceMoveMarkers: true,
      },
    ]);
    editorInstance.pushUndoStop();

    const startPos = model.getPositionAt(nextStart);
    const endPos = model.getPositionAt(nextEnd);
    editorInstance.setSelection(
      new monaco.Selection(
        startPos.lineNumber,
        startPos.column,
        endPos.lineNumber,
        endPos.column,
      ),
    );
    editorInstance.focus();
  };

  const applyLinePrefix = (prefix) => {
    const editorInstance = editorRef.current;
    const monaco = monacoRef.current;
    const model = editorInstance?.getModel();
    const selection = editorInstance?.getSelection();
    if (!editorInstance || !monaco || !model || !selection) return;

    const fullText = model.getValue();
    const startOffset = model.getOffsetAt(selection.getStartPosition());
    const endOffset = model.getOffsetAt(selection.getEndPosition());
    const lineStartOffset = fullText.lastIndexOf("\n", startOffset - 1) + 1;
    const selected = fullText.slice(lineStartOffset, endOffset) || "";
    const prefixed = selected
      .split("\n")
      .map((line, index) =>
        prefix === "1. " ? `${index + 1}. ${line}` : prefix + line,
      )
      .join("\n");
    const rangeStart = model.getPositionAt(lineStartOffset);
    const rangeEnd = model.getPositionAt(endOffset);

    editorInstance.pushUndoStop();
    editorInstance.executeEdits("message-markdown-prefix", [
      {
        range: new monaco.Range(
          rangeStart.lineNumber,
          rangeStart.column,
          rangeEnd.lineNumber,
          rangeEnd.column,
        ),
        text: prefixed,
        forceMoveMarkers: true,
      },
    ]);
    editorInstance.pushUndoStop();

    const cursorPos = model.getPositionAt(lineStartOffset + prefixed.length);
    editorInstance.setSelection(
      new monaco.Selection(
        cursorPos.lineNumber,
        cursorPos.column,
        cursorPos.lineNumber,
        cursorPos.column,
      ),
    );
    editorInstance.focus();
  };

  const runEditorAction = (actionId) => {
    const editorInstance = editorRef.current;
    if (!editorInstance) return;
    editorInstance.focus();
    editorInstance.getAction(actionId)?.run();
  };

  const continueMarkdownList = () => {
    const editorInstance = editorRef.current;
    const monaco = monacoRef.current;
    const model = editorInstance?.getModel();
    const selection = editorInstance?.getSelection();
    if (!editorInstance || !monaco || !model || !selection) return false;
    if (!selection.isEmpty()) return false;

    const position = selection.getPosition();
    const lineContent = model.getLineContent(position.lineNumber);
    const lineBeforeCursor = lineContent.slice(0, position.column - 1);
    const listLine = parseMarkdownListLine(lineContent);
    if (!listLine) return false;

    const isCursorPastPrefix =
      position.column >= listLine.currentPrefix.length + 1;
    if (!isCursorPastPrefix) return false;

    if (listLine.isEmpty) {
      editorInstance.pushUndoStop();
      editorInstance.executeEdits("message-markdown-exit-list", [
        {
          range: new monaco.Range(
            position.lineNumber,
            1,
            position.lineNumber,
            model.getLineMaxColumn(position.lineNumber),
          ),
          text: "",
          forceMoveMarkers: true,
        },
      ]);
      editorInstance.pushUndoStop();

      editorInstance.setSelection(
        new monaco.Selection(position.lineNumber, 1, position.lineNumber, 1),
      );
      editorInstance.focus();
      return true;
    }

    const continuedPrefix = parseMarkdownListLine(lineBeforeCursor)?.nextPrefix;
    if (!continuedPrefix) return false;

    const insertRange = new monaco.Range(
      position.lineNumber,
      position.column,
      position.lineNumber,
      position.column,
    );

    editorInstance.pushUndoStop();
    editorInstance.executeEdits("message-markdown-enter", [
      {
        range: insertRange,
        text: `\n${continuedPrefix}`,
        forceMoveMarkers: true,
      },
    ]);
    editorInstance.pushUndoStop();

    const nextLineNumber = position.lineNumber + 1;
    const nextColumn = continuedPrefix.length + 1;
    editorInstance.setSelection(
      new monaco.Selection(
        nextLineNumber,
        nextColumn,
        nextLineNumber,
        nextColumn,
      ),
    );
    editorInstance.focus();
    return true;
  };

  const handleEditorMount = (editorInstance, monaco) => {
    editorRef.current = editorInstance;
    monacoRef.current = monaco;

    const registerAction = (id, label, keybindings, run) => {
      editorInstance.addAction({
        id,
        label,
        keybindings,
        run,
      });
    };

    registerAction(
      EDITOR_ACTIONS.bold,
      "Bold selection",
      [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyB],
      () => applyWrap("**"),
    );
    registerAction(
      EDITOR_ACTIONS.italic,
      "Italicize selection",
      [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyI],
      () => applyWrap("*"),
    );
    registerAction(
      EDITOR_ACTIONS.heading,
      "Insert heading",
      [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyH],
      () => applyLinePrefix("## "),
    );
    registerAction(
      EDITOR_ACTIONS.bulletList,
      "Insert bulleted list",
      [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.Digit8],
      () => applyLinePrefix("- "),
    );
    registerAction(
      EDITOR_ACTIONS.numberedList,
      "Insert numbered list",
      [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.Digit7],
      () => applyLinePrefix("1. "),
    );
    registerAction(
      EDITOR_ACTIONS.link,
      "Insert link",
      [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyK],
      () => applyWrap("[", "](https://)"),
    );

    editorInstance.onKeyDown((event) => {
      if (event.keyCode !== monaco.KeyCode.Enter) return;
      if (!continueMarkdownList()) return;
      event.preventDefault();
      event.stopPropagation();
    });
  };

  const editorOptions = {
    automaticLayout: true,
    minimap: { enabled: false },
    lineNumbers: "off",
    glyphMargin: false,
    folding: false,
    scrollBeyondLastLine: false,
    wordWrap: "on",
    fontSize: 13.5,
    lineHeight: 21,
    fontFamily: MONO_FONT,
    tabSize: 2,
    renderLineHighlight: "none",
    overviewRulerLanes: 0,
    overviewRulerBorder: false,
    padding: { top: 12, bottom: 12 },
    scrollbar: {
      vertical: "hidden",
      horizontal: "auto",
      alwaysConsumeMouseWheel: false,
    },
    quickSuggestions: false,
    suggestOnTriggerCharacters: false,
    contextmenu: true,
    ariaLabel: "Message body editor",
  };

  const handleSend = async () => {
    if (user?.userRole !== "admin") {
      setSnack({
        open: true,
        severity: "error",
        msg: "Admin access is required to send messages.",
      });
      return;
    }

    setShowErrors(true);
    if (selectedUsers.length === 0 || !subject.trim() || !body.trim()) {
      return;
    }

    const recipientEmails = selectedUsers.map((u) => u.email);

    try {
      setSending(true);
      await supabaseAuthenticatedApi.post(
        "/protected/messages/send",
        {
          subject: subject.trim(),
          body: body.trim(),
          recipientEmails,
        },
        "",
        30000,
      );

      handleDiscard();
      setSnack({
        open: true,
        severity: "success",
        msg: `Message sent to ${recipientEmails.length} recipient${
          recipientEmails.length === 1 ? "" : "s"
        }.`,
      });
    } catch (err) {
      console.error("Error sending message:", err);
      setSnack({
        open: true,
        severity: "error",
        msg: err?.message || "Failed to send message.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <MainPanelContent title="Messages">
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 1.5, // RADIUS.md (12px) — matches the app card radius
          overflow: "hidden",
          boxShadow:
            "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
          // Bounded card with a scrollable middle: the header and footer stay
          // pinned while the form body (incl. the expanding message box) scrolls
          // within the "New message" card, capped at the available height.
          display: "flex",
          flexDirection: "column",
          maxHeight: "100%",
          minHeight: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "#F8FAFC",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>New message</Typography>
          {hasDraft && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Draft saved
            </Typography>
          )}
        </Box>

        {/* Scrollable form body. The message box may expand freely; once the
            card hits its max height, the scroll bar appears here (at the card
            level) rather than inside the textarea. Extra bottom padding keeps
            the message box off the footer border. */}
        <Box sx={{ p: 2.5, pb: 3.5, flex: 1, minHeight: 0, overflowY: "auto" }}>
          {error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Failed to load users: {error}
            </Alert>
          )}

          {/* From (read-only sender identity) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
              color: "text.secondary",
            }}
          >
            <Typography variant="body2" sx={{ width: 40 }}>
              From
            </Typography>
            <Chip
              size="small"
              label="EFabric · efabric@cs.wisc.edu"
              variant="outlined"
            />
          </Box>

          {/* To */}
          <FieldLabel htmlFor="message-recipients">Recipients</FieldLabel>
          <Autocomplete
            id="message-recipients"
            multiple
            options={users}
            value={selectedUsers}
            loading={loading}
            disableCloseOnSelect
            filterSelectedOptions
            popupIcon={null}
            limitTags={expandedRecipients ? -1 : COLLAPSED_TAGS}
            getLimitTagsText={(more) => `+${more} more`}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(e, val) => setSelectedUserIds(val.map((u) => u.id))}
            renderTags={(value, getTagProps) =>
              value.map((u, index) => {
                const name = `${u.first_name || ""} ${
                  u.last_name || ""
                }`.trim();
                return (
                  <Tooltip key={u.id} title={u.email}>
                    <Chip
                      size="small"
                      label={name || u.email}
                      {...getTagProps({ index })}
                      sx={{
                        bgcolor: "#EEF2F6",
                        color: "#334155",
                        fontWeight: 500,
                        border: "1px solid #E2E8F0",
                      }}
                    />
                  </Tooltip>
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedUsers.length ? "" : "Add recipients by name or email…"
                }
                error={recipientsError}
                helperText={
                  recipientsError ? "Select at least one recipient." : " "
                }
              />
            )}
          />

          {/* Group quick-picks (replace the selection with that group) */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 0.5, mb: 2, flexWrap: "wrap", gap: 1 }}
          >
            <GroupsOutlinedIcon
              sx={{ fontSize: 18, color: "text.secondary" }}
            />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Quick pick:
            </Typography>
            <Button
              size="small"
              variant={activeGroup === "everyone" ? "contained" : "outlined"}
              onClick={() => handleQuickAdd("everyone")}
              disabled={loading || users.length === 0}
            >
              Everyone
            </Button>
            <Button
              size="small"
              variant={activeGroup === "admins" ? "contained" : "outlined"}
              onClick={() => handleQuickAdd("admins")}
              disabled={loading || admins.length === 0}
            >
              Admins
            </Button>
            <Button
              size="small"
              variant={activeGroup === "users" ? "contained" : "outlined"}
              onClick={() => handleQuickAdd("users")}
              disabled={loading || nonAdmins.length === 0}
            >
              Users
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={() => setSelectedUserIds([])}
              disabled={selectedUsers.length === 0}
              sx={{ color: "text.secondary" }}
            >
              Clear
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            {selectedUsers.length > COLLAPSED_TAGS && (
              <Button
                size="small"
                variant="text"
                onClick={() => setExpandedRecipients((v) => !v)}
                sx={{ color: "text.secondary" }}
              >
                {expandedRecipients ? "Show less" : "Show all"}
              </Button>
            )}
            <Chip
              size="small"
              label={`${selectedUsers.length} selected`}
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                bgcolor: "#F1F5F9",
              }}
            />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Subject */}
          <FieldLabel htmlFor="message-subject">Subject</FieldLabel>
          <TextField
            id="message-subject"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject line"
            error={subjectError}
            helperText={subjectError ? "Subject is required." : " "}
            sx={{ mb: 1 }}
          />

          {/* Body: Markdown toolbar + Write/Preview */}
          <FieldLabel>Message</FieldLabel>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Tooltip title="Bold (Ctrl/Cmd+B)">
                <span>
                  <IconButton
                    size="small"
                    disabled={bodyTab !== "write"}
                    onClick={() => runEditorAction(EDITOR_ACTIONS.bold)}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Italic (Ctrl/Cmd+I)">
                <span>
                  <IconButton
                    size="small"
                    disabled={bodyTab !== "write"}
                    onClick={() => runEditorAction(EDITOR_ACTIONS.italic)}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Heading (Ctrl/Cmd+Shift+H)">
                <span>
                  <IconButton
                    size="small"
                    disabled={bodyTab !== "write"}
                    onClick={() => runEditorAction(EDITOR_ACTIONS.heading)}
                  >
                    <TitleIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 0.5, my: 0.5 }}
              />
              <Tooltip title="Bulleted list (Ctrl/Cmd+Shift+8)">
                <span>
                  <IconButton
                    size="small"
                    disabled={bodyTab !== "write"}
                    onClick={() => runEditorAction(EDITOR_ACTIONS.bulletList)}
                  >
                    <FormatListBulletedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Numbered list (Ctrl/Cmd+Shift+7)">
                <span>
                  <IconButton
                    size="small"
                    disabled={bodyTab !== "write"}
                    onClick={() => runEditorAction(EDITOR_ACTIONS.numberedList)}
                  >
                    <FormatListNumberedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Link (Ctrl/Cmd+K)">
                <span>
                  <IconButton
                    size="small"
                    disabled={bodyTab !== "write"}
                    onClick={() => runEditorAction(EDITOR_ACTIONS.link)}
                  >
                    <InsertLinkIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            <Tabs
              value={bodyTab}
              onChange={(e, v) => setBodyTab(v)}
              sx={{
                minHeight: 0,
                "& .MuiTab-root": {
                  minHeight: 0,
                  py: 0.5,
                  fontSize: 13,
                  textTransform: "none",
                },
              }}
            >
              <Tab value="write" label="Write" />
              <Tab value="preview" label="Preview" />
            </Tabs>
          </Box>

          {bodyTab === "write" ? (
            <Box>
              <Box
                sx={{
                  border: 1,
                  borderColor: bodyError ? "error.main" : "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                  bgcolor: "#FFFFFF",
                  transition: "border-color 0.15s ease",
                  "&:focus-within": {
                    borderColor: bodyError ? "error.main" : "primary.main",
                  },
                }}
              >
                <Editor
                  height={`${MESSAGE_EDITOR_HEIGHT}px`}
                  defaultLanguage="markdown"
                  theme={EDITOR_THEME_LIGHT_NAME}
                  beforeMount={defineLightEditorTheme}
                  value={body}
                  onChange={(value) => setBody(value ?? "")}
                  onMount={handleEditorMount}
                  options={editorOptions}
                  loading={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  }
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.75,
                  minHeight: 20,
                  color: bodyError ? "error.main" : "text.secondary",
                }}
              >
                {bodyError
                  ? "Message body is required."
                  : "Markdown supported. Use the toolbar or keyboard shortcuts for formatting."}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: bodyError ? "error.main" : "divider",
                borderRadius: 1,
                p: 2,
                minHeight: 208,
                fontSize: 14,
                lineHeight: 1.6,
                "& h1, & h2, & h3": { mt: 1, mb: 0.5, lineHeight: 1.3 },
                "& p": { my: 1 },
                "& ul, & ol": { pl: 3, my: 1 },
                "& blockquote": {
                  borderLeft: "3px solid",
                  borderColor: "divider",
                  pl: 1.5,
                  my: 1,
                  color: "text.secondary",
                },
                "& a": { color: "primary.main" },
                "& code": {
                  bgcolor: "#F1F5F9",
                  px: 0.5,
                  borderRadius: 0.5,
                  fontSize: "0.85em",
                },
              }}
            >
              {body.trim() ? (
                <div
                  dangerouslySetInnerHTML={{ __html: renderMessageHtml(body) }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nothing to preview yet.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "#F8FAFC",
          }}
        >
          <Button
            variant="text"
            color="inherit"
            onClick={handleDiscard}
            disabled={!hasDraft || sending}
            sx={{ color: "text.secondary" }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending}
            startIcon={
              sending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendRoundedIcon />
              )
            }
          >
            {sending ? "Sending…" : "Send"}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </MainPanelContent>
  );
};

MessagePage.propTypes = {
  user: PropTypes.shape({
    userRole: PropTypes.string,
  }),
};

export default MessagePage;
